import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { IoRefreshOutline } from 'react-icons/io5';

import { Table } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  ProviderHealth,
  ThirdPartyLog,
  ThirdPartySummary,
  dateInputToIso,
  durationTone,
  fetchLogProviders,
  fetchThirdPartyLogs,
  formatDurationMs,
  formatProviderLabel,
  statusTone,
} from 'src/api/adminLogs';
import { convertDate } from 'src/components/Table/helpers';
import { useFilterQueryEffect, type FilterQueryParams } from 'src/hooks/useFilterQueryEffect';

import LogDetailModal from './LogDetailModal';
import {
  DurationText,
  OkBadge,
  ProviderCard,
  ProviderGrid,
  ProviderMeta,
  ProviderName,
  SpinIcon,
  StatCard,
  StatGrid,
  StatLabel,
  StatValue,
  StatusCode,
  ToolButton,
  ToolbarActionButtons,
  ToolbarActions,
  ToolbarRow,
} from './styled';

// "Yes" narrows to failed calls (ok=false), "No" to successful ones (ok=true).
const FAILURES_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

// One bar per filter; the API ANDs them. Status code and min duration are
// numbers on the API, so a partial entry is held back until it is one.
const THIRD_PARTY_FILTERS: FilterDef[] = [
  { key: 'provider', label: 'Provider', options: [], allLabel: 'All providers' },
  { key: 'failures_only', label: 'Failures only', options: FAILURES_OPTIONS, allLabel: 'All calls' },
  { key: 'status_code', label: 'Status code', placeholder: 'e.g. 403' },
  { key: 'min_duration_ms', label: 'Min duration ms', placeholder: 'e.g. 2000' },
  { key: 'from', label: 'From', type: 'date' },
  { key: 'to', label: 'To', type: 'date' },
];

// A whole HTTP status (100-599) — the API rejects anything else.
const HTTP_STATUS = /^[1-5]\d{2}$/;
const INTEGER = /^\d+$/;

const HEADERS = ['Time', 'Provider', 'Call', 'Status', 'Duration', 'OK'];
const POLL_MS = 45_000;

type ThirdPartyTabProps = {
  onTotalChange?: Dispatch<SetStateAction<number | null>>;
};

const ThirdPartyTab: React.FC<ThirdPartyTabProps> = ({ onTotalChange }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<ThirdPartyLog[]>([]);
  const [summary, setSummary] = useState<ThirdPartySummary>({
    avg_duration_ms: 0,
    max_duration_ms: 0,
    failures: 0,
  });
  const [providers, setProviders] = useState<ProviderHealth[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<ThirdPartyLog | null>(null);
  // The last query the hook ran, so polling and the refresh button repeat it.
  const lastQueryRef = useRef<FilterQueryParams>({ page: 1, filters: {} });

  const loadProviders = useCallback(async () => {
    try {
      const res = await fetchLogProviders();
      setProviders(res.providers);
    } catch {
      // Picker still works with "All providers" if this fails.
    }
  }, []);

  const load = useCallback(
    async (params: FilterQueryParams, silent = false) => {
      lastQueryRef.current = params;
      const {
        provider,
        failures_only,
        status_code = '',
        min_duration_ms = '',
        from = '',
        to = '',
      } = params.filters;
      if (!silent) setLoading(true);
      try {
        const res = await fetchThirdPartyLogs({
          page: params.page,
          limit: 20,
          provider: provider || undefined,
          ok: failures_only === 'true' ? false : failures_only === 'false' ? true : undefined,
          status_code: HTTP_STATUS.test(status_code) ? status_code : undefined,
          min_duration_ms: INTEGER.test(min_duration_ms) ? min_duration_ms : undefined,
          from: dateInputToIso(from),
          to: dateInputToIso(to, true),
        });
        setLogs(res.third_party_logs);
        setSummary(res.summary);
        setTotalPages(Math.max(1, res.meta?.page_count || 1));
        onTotalChange?.(res.meta?.total_count || 0);
      } catch (e: any) {
        if (!silent) {
          dispatch(
            setAppAlert({
              message: e?.response?.data?.error ?? 'Failed to load third-party logs',
              isSuccess: false,
            }),
          );
          setLogs([]);
          onTotalChange?.(0);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [dispatch, onTotalChange],
  );

  const { refresh: refreshQuery } = useFilterQueryEffect({ filters, currentPage, setCurrentPage, query: load });

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    const id = window.setInterval(() => {
      load(lastQueryRef.current, true);
      loadProviders();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [load, loadProviders]);

  const thirdPartyFilters = useMemo(
    () =>
      THIRD_PARTY_FILTERS.map((filter) =>
        filter.key === 'provider'
          ? {
              ...filter,
              options: providers.map((p) => ({
                value: p.provider,
                label: formatProviderLabel(p.provider),
              })),
            }
          : filter,
      ),
    [providers],
  );

  const rows = useMemo(
    () =>
      logs.map((log) => ({
        ...log,
        time_display: log.created_at
          ? `${convertDate(log.created_at).date} ${convertDate(log.created_at).time}`
          : '—',
        provider_display: formatProviderLabel(log.provider),
        call_display: log.url || log.context || '—',
        status_display: log.status_code != null ? String(log.status_code) : '—',
        duration_display: formatDurationMs(log.duration_ms),
        ok_display: log.ok ? 'OK' : 'Fail',
      })),
    [logs],
  );

  const setFilter = (key: string, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const refresh = () => {
    load(lastQueryRef.current);
    loadProviders();
  };

  const activeProvider = filters.provider ?? '';

  return (
    <>
      {providers.length > 0 && (
        <ProviderGrid>
          {providers.map((p) => {
            const failPct = p.calls > 0 ? Math.round((p.failures / p.calls) * 100) : 0;
            return (
              <ProviderCard
                key={p.provider}
                type="button"
                $active={activeProvider === p.provider}
                $hasFailures={p.failures > 0}
                // The health cards double as a provider toggle for the bar below.
                onClick={() => setFilter('provider', activeProvider === p.provider ? '' : p.provider)}
              >
                <ProviderName>{formatProviderLabel(p.provider)}</ProviderName>
                <ProviderMeta>
                  {p.calls} calls · {failPct}% fail
                  <br />
                  avg {formatDurationMs(p.avg_duration_ms)}
                </ProviderMeta>
              </ProviderCard>
            );
          })}
        </ProviderGrid>
      )}

      <StatGrid>
        <StatCard>
          <StatLabel>Avg duration</StatLabel>
          <StatValue>{formatDurationMs(summary.avg_duration_ms)}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Max duration</StatLabel>
          <StatValue>{formatDurationMs(summary.max_duration_ms)}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Failures</StatLabel>
          <StatValue>{summary.failures ?? 0}</StatValue>
        </StatCard>
      </StatGrid>

      <FilterBar
        filters={thirdPartyFilters}
        values={filters}
        onChange={setFilter}
        onClear={() => setFilters({})}
        onRefresh={refreshQuery}
      />
      <ToolbarRow $flush>
        <ToolbarActions>
          <ToolbarActionButtons>
            <ToolButton
              type="button"
              $iconOnly
              disabled={loading}
              onClick={refresh}
              title="Refresh logs"
              aria-label="Refresh logs"
            >
              <SpinIcon $spin={loading}>
                <IoRefreshOutline size={18} />
              </SpinIcon>
            </ToolButton>
          </ToolbarActionButtons>
        </ToolbarActions>
      </ToolbarRow>

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={HEADERS}
          items={rows}
          columns="1.1fr 0.9fr 1.6fr 0.55fr 0.7fr 0.5fr"
          fieldsToShow={[
            'time_display',
            'provider_display',
            'call_display',
            'status_display',
            'duration_display',
            'ok_display',
          ]}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          onRowClick={(item) => setSelected(item as ThirdPartyLog)}
          renderCell={(item, fieldName) => {
            if (fieldName === 'status_display') {
              return (
                <StatusCode $tone={statusTone(item.status_code)}>{item.status_code ?? '—'}</StatusCode>
              );
            }
            if (fieldName === 'duration_display') {
              return (
                <DurationText $tone={durationTone(item.duration_ms)}>
                  {item.duration_display}
                </DurationText>
              );
            }
            if (fieldName === 'ok_display') {
              return <OkBadge $ok={Boolean(item.ok)}>{item.ok ? 'OK' : 'Fail'}</OkBadge>;
            }
            if (fieldName === 'call_display') {
              return <span title={item.call_display}>{item.call_display}</span>;
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}

      {selected && (
        <LogDetailModal kind="third-party" log={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
};

export default ThirdPartyTab;
